import React from 'react';
import ReactDOM from 'react-dom';
import { CardColor } from '../types';

const TargetingArrow: React.FC<{
  start: { x: number, y: number },
  end: { x: number, y: number },
  color: CardColor,
  isOverTarget?: boolean
}> = ({ start, end, color, isOverTarget = false }) => {
  const portalRoot = document.getElementById('portal-root');
  if (!portalRoot) return null;

  const colorMap: Record<CardColor, string> = {
    [CardColor.Red]: '#ef4444',
    [CardColor.Pink]: '#ec4899',
    [CardColor.Green]: '#22c55e',
    [CardColor.Black]: '#94a3b8',
    [CardColor.Purple]: '#a855f7',
    [CardColor.Yellow]: '#eab308',
  };
  const strokeColor = colorMap[color] || '#ffffff';

  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const distance = Math.sqrt(dx * dx + dy * dy) || 1;
  const curveFactor = Math.min(100, 6000 / (distance + 1));
  const midX = (start.x + end.x) / 2;
  const midY = (start.y + end.y) / 2;
  const perpX = -dy / distance;
  const perpY = dx / distance;
  const controlX = midX + perpX * curveFactor;
  const controlY = midY + perpY * curveFactor;
  const pathData = `M ${start.x} ${start.y} Q ${controlX} ${controlY} ${end.x} ${end.y}`;

  // sizes change when over target
  const outerR = isOverTarget ? 28 : 22;
  const midR = isOverTarget ? 16 : 15;
  const innerR = isOverTarget ? 10 : 6;
  const outerStroke = isOverTarget ? 3 : 2;

  const arrowSvg = (
    <svg className="fixed inset-0 pointer-events-none w-full h-full" style={{ zIndex: 9999 }}>
      <defs>
        <filter id="glow-effect">
          <feGaussianBlur stdDeviation="3.5" result="coloredBlur" />
          <feMerge>
            <feMergeNode in="coloredBlur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
        <marker
          id="arrowhead"
          viewBox="-2 -5 10 10"
          refX="8"
          refY="0"
          markerWidth="6"
          markerHeight="6"
          orient="auto"
        >
          <path d="M 0,-4 L 8,0 L 0,4" fill={strokeColor} />
        </marker>
      </defs>

      {/* curved dashed path */}
      <path
        d={pathData}
        stroke={strokeColor}
        strokeWidth="4"
        fill="none"
        strokeLinecap="round"
        filter="url(#glow-effect)"
        markerEnd="url(#arrowhead)"
        style={{
          strokeDasharray: '10 10',
          animation: 'marching-ants 0.5s linear infinite'
        }}
      />

      {/* pulsating reticle at the end point */}
      <g transform={`translate(${end.x}, ${end.y})`} style={{ transition: 'transform 0.05s' }}>
        {/* outer ring (subtle fill when over) */}
        <circle r={outerR} stroke={strokeColor} strokeWidth={outerStroke} fill={isOverTarget ? strokeColor : 'none'} fillOpacity={isOverTarget ? 0.12 : 0} opacity={isOverTarget ? 0.95 : 0.9} />

        {/* middle ring */}
        <circle r={midR} stroke={strokeColor} strokeWidth="2" fill="none" />

        {/* crosshair */}
        <path d="M-10 0 L10 0 M0 -10 L0 10" stroke={strokeColor} strokeWidth="2" />

        {/* small pulsing dot (animated with SVG <animate>) */}
        <circle r={innerR} fill={strokeColor} opacity="0.95">
          <animate
            attributeName="r"
            values={`${innerR}; ${innerR + (isOverTarget ? 6 : 4)}; ${innerR}`}
            dur="0.9s"
            repeatCount="indefinite"
          />
          <animate
            attributeName="opacity"
            values="0.95;0.35;0.95"
            dur="0.9s"
            repeatCount="indefinite"
          />
        </circle>
      </g>
    </svg>
  );

  return ReactDOM.createPortal(arrowSvg, portalRoot);
};

export default TargetingArrow;
